/**
 * Vue shell for the shared “new note / new database entry” create dialog.
 *
 * ## Who calls this
 *
 * - `OutlineNotes.vue` → `noteFlow` (“New note” dialog; creates under Notes/)
 * - `OutlineDatabase.vue` → `databaseFlow` (“New worldbuilding entry” dialog)
 *
 * Chapter/act create is **not** here. That lives in `OutlineTree.vue` (folder
 * dedup + pending scene-drop after create).
 *
 * ## How callers wire it
 *
 * 1. Pass an input ref (for focus), live `existingNames`, `normalizeName`
 *    (e.g. append `.zwdoc`), error copy, and `onCreate(finalName)` (workspace
 *    invoke + refresh).
 * 2. Bind the dialog: `:open="flow.open.value"`, `v-model="flow.draft.value"`,
 *    show `flow.error`, buttons call `flow.commit` / `flow.cancel`.
 * 3. “New …” buttons call `flow.start()`. Context menus / outline cancel
 *    hooks call `flow.cancel()`.
 *
 * ## TEA split (#237)
 *
 * - Pure Model / Msg / update / Cmd → `useCreateEntityFlow.update.ts`
 * - This file → `useTea` + Cmd runner (`focusInput`, `create`)
 * - `draft` is a writable computed so `v-model` dispatches `DraftChanged`
 */
import {
  computed,
  nextTick,
  type ComputedRef,
  type Ref,
  type WritableComputedRef,
} from "vue";
import { assertNever } from "../lib/assertNever";
import { isJust, just, type Maybe } from "../lib/maybe";
import { useTea } from "../lib/tea";
import {
  initialCreateEntityModel,
  makeCreateEntityUpdate,
  type CreateEntityCmd,
} from "./useCreateEntityFlow.update";

type FocusableInput = {
  focus: () => void;
  select: () => void;
};

/**
 * @returns Dialog bindings: `open` / `draft` / `error`, plus `start` / `cancel` / `commit`.
 */
export function useCreateEntityFlow(options: {
  inputRef: Ref<Maybe<FocusableInput>>;
  existingNames: Ref<string[]>;
  normalizeName: (raw: string) => string;
  emptyNameError: string;
  duplicateError: string;
  onCreate: (finalName: string) => void | Promise<void>;
}): {
  open: ComputedRef<boolean>;
  draft: WritableComputedRef<string>;
  error: ComputedRef<string>;
  start: () => void;
  cancel: () => void;
  commit: () => void;
} {
  const update = makeCreateEntityUpdate({
    existingNames: () => options.existingNames.value,
    normalizeName: options.normalizeName,
    emptyNameError: options.emptyNameError,
    duplicateError: options.duplicateError,
  });

  async function runCmd(cmd: CreateEntityCmd): Promise<void> {
    switch (cmd.kind) {
      case "focusInput":
        await nextTick();
        if (isJust(options.inputRef.value)) {
          options.inputRef.value.value.focus();
          options.inputRef.value.value.select();
        }
        return;
      case "create":
        await options.onCreate(cmd.name);
        return;
      default:
        assertNever(cmd);
    }
  }

  const { model, dispatch } = useTea(initialCreateEntityModel, update, {
    runCmd: just(runCmd),
  });

  const open = computed(() => model.value.open);
  const error = computed(() => model.value.error);
  const draft = computed({
    get: (): string => model.value.draft,
    set: (value: string): void => {
      void dispatch({ type: "DraftChanged", draft: value });
    },
  });

  function start(): void {
    void dispatch({ type: "Start" });
  }

  function cancel(): void {
    void dispatch({ type: "Cancel" });
  }

  function commit(): void {
    void dispatch({ type: "Commit" });
  }

  return { open, draft, error, start, cancel, commit };
}
